import {observable, autorun} from 'mobx';
import {active_vm_frame} from '../activeVM.js';


// X11 keysym for Escape — the same value the webconnect on-screen keyboard
// layout uses (guacamole-webconnect-ext/layouts/en-us-qwerty-v2c.json).
const ESC_KEYSYM = 65307;


// Whether this browser exposes the Keyboard Lock API, which is what holds
// browser-reserved keys (Escape, Ctrl+W, Ctrl+T, F11...) inside the page while
// fullscreen. Chrome/Edge yes; Firefox, Safari and every mobile browser no.
//
// Firefox 151+ instead honours a keyboardLock option on requestFullscreen. That
// is deliberately NOT reflected here, because it cannot be detected -- see
// can_capture_reserved_keys below.
export const keyboard_lock_supported = () =>
  'keyboard' in navigator && typeof navigator.keyboard.lock === 'function';


// Whether real (Fullscreen API) fullscreen is available. Where it is not, we do
// the CSS maximize alone: entering real fullscreen without a way to capture
// Escape is strictly worse than not entering it, because the browser eats
// Escape and the VM never sees it.
export const real_fullscreen_supported = () =>
  !!(document.body.requestFullscreen && document.fullscreenEnabled);


// The gate for offering real fullscreen at all. Note this is deliberately NOT
// just real_fullscreen_supported(): Safari 16.4+ has unprefixed
// requestFullscreen but no Keyboard Lock, and entering fullscreen without
// capture is strictly worse than staying windowed, because the browser eats
// Escape and the VM never sees it.
//
// Firefox 151+ honours the keyboardLock option on requestFullscreen but does
// not ship navigator.keyboard, and that option is not feature-detectable
// (engines ignore unknown dictionary members rather than throwing). So Firefox
// currently falls back to the CSS maximize. That is safe, just not optimal --
// it needs a narrow Firefox check to opt in, which is a UA sniff we chose not
// to add.
export const can_capture_reserved_keys = () =>
  real_fullscreen_supported() && keyboard_lock_supported();


// 'off' | 'locked' | 'rejected' | 'unsupported'
// A rejection is a real state, not a swallowed error: Chrome 130+ can gate
// lock() behind a permission, and when that happens Escape exits fullscreen
// instead of reaching the VM. The soft-key Esc is the fallback for those cases.
export const keyboard_capture = observable.box('off');


const lock_keyboard = async () => {

  if(!keyboard_lock_supported()) {

    keyboard_capture.set('unsupported');

    return;

  }

  try {

    // No arguments: capture every lockable key, not just Escape. We never try
    // to infer which app the user is in.
    await navigator.keyboard.lock();

    keyboard_capture.set('locked');

    arm_exit_gestures();

  } catch(e) {

    console.log('keyboard.lock() rejected:', e);

    keyboard_capture.set('rejected');

  }

};


// Enter fullscreen and keep browser-reserved keys inside the session.
//
// Firefox 151+ takes the keyboardLock option on requestFullscreen. Chrome
// ignores unknown dictionary members instead of throwing, so that first call
// resolving proves nothing about capture — we always follow up with
// navigator.keyboard.lock(), whichever path succeeded.
export const enter_real_fullscreen = async (element) => {

  try {

    await element.requestFullscreen({keyboardLock: 'browser'});

  } catch(e) {

    try {

      await element.requestFullscreen();

    } catch(e2) {

      console.log('requestFullscreen failed:', e2);

      return false;

    }

  }

  await lock_keyboard();

  // A lock we did not get means Escape exits fullscreen instead of reaching the
  // VM -- the bug this whole path exists to fix. Retreat to the CSS maximize
  // rather than sit in fullscreen with Escape broken.
  if(keyboard_capture.get() !== 'locked') {

    try {

      if(document.fullscreenElement) await document.exitFullscreen();

    } catch(e) {

      console.log('exitFullscreen after failed lock:', e);

    }

    return false;

  }

  return true;

};


// --- exiting fullscreen while keys are captured -----------------------------
//
// Chrome's own escape hatch (hold Escape ~2s) needs ONE sustained keydown. Some
// platforms never deliver one: X11 auto-repeat, and the remote-desktop layers
// often sitting in front of it, synthesise keyup+keydown PAIRS instead, every
// event reporting repeat=false. Measured on such a box: a pair every ~82ms,
// with only ~1ms between each keyup and the keydown that follows it.
//
// That 1ms is the tell we use to reconstruct what the key is really doing. A
// genuine re-press always leaves a human-sized gap after the keyup; auto-repeat
// does not:
//
//   keyup -> keydown within REPEAT_GAP_MS = still the same physical hold
//   keyup -> keydown after  REPEAT_GAP_MS = a real new press
//
// Two gestures fall out of that:
//
//   hold        one press held for HOLD_MS. Offered only where the platform
//               cannot report holds itself -- a single repeat=true keydown
//               proves it can, and we stand down for Chrome's own hatch.
//               Measured from the FIRST keydown of the hold, so the platform's
//               auto-repeat delay is not added on top of HOLD_MS.
//
//   double-tap  two real presses within DOUBLE_TAP_MS. Always offered, since no
//               browser provides this gesture. Auto-repeat cannot trigger it,
//               because repeat pairs fail the REPEAT_GAP_MS test above.
//
// Known trade-off: two deliberate Escapes inside the VM within DOUBLE_TAP_MS
// will also leave fullscreen. Tighten DOUBLE_TAP_MS if that proves annoying.

const HOLD_MS = 1500;
const DOUBLE_TAP_MS = 400;
const REPEAT_GAP_MS = 40;

// We listen in two documents (see arm_exit_gestures), and while the keyboard is
// locked one physical press is delivered to BOTH -- about 1ms apart. Without
// collapsing those, a single Escape reads as an instant double-tap. No human
// can press twice inside this window, so it is safe to treat anything closer
// than DEDUPE_MS as the same keystroke seen twice.
const DEDUPE_MS = 20;

// All four windows above are measured with Date.now(), NOT event.timeStamp.
// timeStamp is relative to the time origin of the document the event was
// dispatched in, and we listen in two of them (see arm_exit_gestures) -- the
// session iframe is re-created on reconnect, so its origin can end up a long
// way from the top document's. Chrome happens to normalise input timestamps to
// the top document, but the spec does not require that, and Date.now() is the
// same value in every frame and every engine. It is not monotonic, so a system
// clock jump mid-gesture could misread one; that is a fair trade at these
// thresholds against a skew that silently breaks every comparison.

// FullscreenWrapper registers its own exit_fullscreen here at import time, so a
// gesture leaves fullscreen exactly the way the toolbar button does: classes
// cleared, secondary windows told to follow, lock released. Calling
// document.exitFullscreen() directly also works -- the fullscreenchange listener
// in FullscreenWrapper catches up -- but that coupling is invisible from here,
// and would break silently if the listener ever moved. Registered rather than
// imported because FullscreenWrapper already imports this module.
let app_exit_fullscreen = null;

export const set_fullscreen_exit_handler = (fn) => { app_exit_fullscreen = fn; };


let native_repeats_seen = false;
let press_started_at = 0;
let last_keyup_at = 0;
let last_keydown_at = 0;
let watched = [];


const leave_fullscreen = (why) => {

  press_started_at = 0;

  try {

    if(app_exit_fullscreen) {

      app_exit_fullscreen();

      return;

    }

    // No host registered (unit test, or this module imported on its own).
    if(document.fullscreenElement) document.exitFullscreen();

  } catch(e) {

    console.log('exit fullscreen (' + why + ') failed:', e);

  }

};


const on_keyup = (e) => {

  if(e.key === 'Escape') last_keyup_at = Date.now();

};


const on_keydown = (e) => {

  // The platform reports held keys properly, so Chrome's hatch works and our
  // hold gesture must keep out of its way. Double-tap still applies.
  if(e.repeat) {

    native_repeats_seen = true;

    return;

  }

  if(e.key !== 'Escape') {

    press_started_at = 0;

    return;

  }

  const now = Date.now();

  // Same keystroke arriving from the second document.
  if(last_keydown_at && (now - last_keydown_at) <= DEDUPE_MS) return;

  last_keydown_at = now;

  const same_hold = last_keyup_at && (now - last_keyup_at) <= REPEAT_GAP_MS;

  if(!same_hold) {

    if(press_started_at && (now - press_started_at) <= DOUBLE_TAP_MS) {

      leave_fullscreen('double-tap');

      return;

    }

    press_started_at = now;

  }

  if(!native_repeats_seen && press_started_at && (now - press_started_at) >= HOLD_MS) {

    leave_fullscreen('hold');

  }

};


// Listen in both documents: with the lock held the top document sees the keys,
// but focus lives in the session iframe, so cover that too where the
// document.domain relaxation makes it reachable (see cross-site/domain.js).
const arm_exit_gestures = () => {

  if(watched.length) return;

  press_started_at = 0;
  last_keyup_at = 0;
  last_keydown_at = 0;

  const docs = [document];

  try {

    const iframe = active_vm_frame.get();

    if(iframe && iframe.contentDocument) docs.push(iframe.contentDocument);

  } catch(e) {

    // Cross-origin without the relaxation (localhost dev); the top document
    // listener still covers the locked case.
  }

  docs.forEach(d => {

    d.addEventListener('keydown', on_keydown, true);
    d.addEventListener('keyup', on_keyup, true);

    watched.push(d);

  });

};


const disarm_exit_gestures = () => {

  watched.forEach(d => {

    try {

      d.removeEventListener('keydown', on_keydown, true);
      d.removeEventListener('keyup', on_keyup, true);

    } catch(e) {}

  });

  watched = [];

};


// Drop the capture. Safe to call when nothing is held.
export const release_keyboard_lock = () => {

  if(keyboard_capture.get() === 'off') return;

  disarm_exit_gestures();

  try {

    if('keyboard' in navigator && typeof navigator.keyboard.unlock === 'function') navigator.keyboard.unlock();

  } catch(e) {

    console.log('keyboard.unlock() failed:', e);

  }

  keyboard_capture.set('off');

};


// Send one Escape to the VM. Universal fallback: needs no lock, no fullscreen,
// and works on browsers that have neither API. Same iframe reach-in as
// resize.js — the guac client hangs off the .display scope.
export const send_esc_to_vm = () => {

  const iframe = active_vm_frame.get();

  if(!iframe || !iframe.contentWindow || !iframe.contentWindow.angular) return false;

  try {

    const client = iframe.contentWindow.angular.element('.display').scope().client.client;

    client.sendKeyEvent(1, ESC_KEYSYM);
    client.sendKeyEvent(0, ESC_KEYSYM);

    return true;

  } catch(e) {

    console.log('send_esc_to_vm failed:', e);

    return false;

  }

};


// Never leave the browser capturing keys after the session goes away.
// Same autorun bootstrap as multimon.js / resize.js.
window.setTimeout(() => autorun(() => {

  if(!active_vm_frame.get()) release_keyboard_lock();

}), 0);
